import React from 'react';

interface SmartPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  itemType: string; // e.g., "products", "factories", "test labs", "test reports"
  onPageChange: (page: number) => void;
}

interface PaginationItem {
  type: 'page' | 'ellipsis';
  page?: number;
  active?: boolean;
  key?: string;
}

const SmartPagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  itemType,
  onPageChange
}: SmartPaginationProps) => {
  // Don't render if only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  // Generate smart pagination with ellipsis for large page counts
  const generatePaginationItems = (): PaginationItem[] => {
    const items: PaginationItem[] = [];
    const maxVisiblePages = 5; // Show max 5 page buttons
    const sidePages = 1; // Show 1 page on each side of current page

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push({
          type: 'page',
          page: i,
          active: i === currentPage
        });
      }
    } else {
      // Always show first page
      items.push({
        type: 'page',
        page: 1,
        active: currentPage === 1
      });

      // Add ellipsis after first page if needed
      if (currentPage > sidePages + 2) {
        items.push({ type: 'ellipsis', key: 'start-ellipsis' });
      }

      // Add pages around current page
      const startPage = Math.max(2, currentPage - sidePages);
      const endPage = Math.min(totalPages - 1, currentPage + sidePages);

      for (let i = startPage; i <= endPage; i++) {
        items.push({
          type: 'page',
          page: i,
          active: i === currentPage
        });
      }

      // Add ellipsis before last page if needed
      if (currentPage < totalPages - sidePages - 1) {
        items.push({ type: 'ellipsis', key: 'end-ellipsis' });
      }

      // Always show last page (if it's not already shown)
      if (totalPages > 1) {
        items.push({
          type: 'page',
          page: totalPages,
          active: currentPage === totalPages
        });
      }
    }

    return items;
  };

  const paginationItems = generatePaginationItems();
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startItem} to {endItem} of {totalItems} {itemType}
      </div>
      <div className="pagination-controls">
        <button 
          className="pagination-btn" 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        
        {paginationItems.map((item, index) => {
          if (item.type === 'ellipsis') {
            return (
              <span key={item.key || `ellipsis-${index}`} className="pagination-ellipsis">
                ...
              </span>
            );
          }
          
          return (
            <button
              key={item.page}
              className={`pagination-btn ${item.active ? 'active' : ''}`}
              onClick={() => onPageChange(item.page!)}
            >
              {item.page}
            </button>
          );
        })}
        
        <button 
          className="pagination-btn" 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SmartPagination;